import { InAppState } from "../base/InAppState";
import template from './PremiumTemplate.html';

const LEMON_JS_URL = 'https://app.lemonsqueezy.com/js/lemon.js';
const LEMON_JS_TIMEOUT_MS = 15000;
let lemonJsPromise = null;

/**
 * Load lemon.js once, only when a checkout is started (no request to Lemon Squeezy before).
 * A failed load resets the promise so the next attempt retries.
 */
function loadLemonJs()
{
    if ( !lemonJsPromise )
    {
        lemonJsPromise = new Promise( ( resolve, reject ) =>
        {
            const script = document.createElement( 'script' );
            const fail = () =>
            {
                clearTimeout( timer );
                script.remove();
                lemonJsPromise = null;
                reject( new Error( 'lemon.js could not be loaded' ) );
            };
            const timer = setTimeout( fail, LEMON_JS_TIMEOUT_MS );
            script.src = LEMON_JS_URL;
            script.async = true;
            script.onload = () => { clearTimeout( timer ); resolve(); };
            script.onerror = fail;
            document.head.appendChild( script );
        } );
    }

    return lemonJsPromise;
}

export class PremiumState extends InAppState
{
    static ROUTE = '/premium';
    static ID = 'admin-premium';

    async enter()
    {
        this._exited = false;
        this.app.view.blockUI( this.app.view.pageContent, 'Loading subscription...' );

        this.currentPackage = 'FREE';
        this.premiumData = null;

        // Fetch current subscription from the organisation
        const organisationId = this.app.user.organisationId;
        if ( organisationId )
        {
            const { result } = await this.app.api.get( `/organisation/${organisationId}` );
            if ( result.status === 200 && result.data )
            {
                this.premiumData = result.data.premium || null;
                this.currentPackage = this.premiumData?.id || 'FREE';
            }
        }

        this._render();
        this.app.view.unblockUI( this.app.view.pageContent );

        // Check for success query param (redirect from LemonSqueezy checkout)
        const urlParams = new URLSearchParams( window.location.search );
        if ( urlParams.has( 'success' ) )
        {
            // Clean up the URL without reloading
            window.history.replaceState( {}, '', window.location.pathname );
            this._onCheckoutSuccess();
        }
    }

    _setupLemonSqueezy()
    {
        if ( typeof window.createLemonSqueezy === 'function' )
        {
            window.createLemonSqueezy();
        }

        window.LemonSqueezy?.Setup( {
            eventHandler: ( event ) =>
            {
                if ( event.event === 'Checkout.Success' )
                {
                    this._onCheckoutSuccess();
                }
            }
        } );
    }

    async _onCheckoutSuccess()
    {
        if ( this._confirmingCheckout || this._exited ) return;
        this._confirmingCheckout = true;
        this.app.view.toast( 'Checking your subscription activation...', 'info' );
        try
        {
            // A redirect or overlay event is not proof of payment. Wait for the
            // signed webhook to update the organisation on the server.
            for ( let attempt = 0; attempt < 10 && !this._exited; attempt++ )
            {
                const refreshed = await this._refreshOrganisationData().catch( () => false );
                if ( this._exited ) return;
                if ( refreshed && this.premiumData?.lsSubscriptionId && this.currentPackage !== 'FREE' &&
                    [ 'active', 'on_trial' ].includes( this.premiumData.status ) )
                {
                    this._showSuccessModal();
                    return;
                }
                if ( attempt < 9 ) await new Promise( resolve => setTimeout( resolve, 3000 ) );
            }
            if ( !this._exited ) this.app.view.toast( 'Activation is not confirmed yet. Please refresh this page shortly. Do not pay again; contact support if your plan remains unchanged.', 'info' );
        }
        finally { this._confirmingCheckout = false; }
    }

    _render()
    {
        this.app.view.render(
            this.app.view.pageContent,
            template,
            {
                currentPackage: this.currentPackage,
                premiumData: this.premiumData
            }
        );

        this._bindEventListeners();
    }

    _bindEventListeners()
    {
        this._billingCycle = 'yearly';

        // Billing toggle
        const toggle = this.app.view.pageContent.querySelector( '[data-type="billing-toggle"]' );
        if ( toggle )
        {
            this.addEventListener( toggle, 'change', () => {
                this._billingCycle = toggle.checked ? 'yearly' : 'monthly';
                this._updatePriceDisplay();
            });
        }

        // Plan selection buttons
        this.app.view.pageContent.querySelectorAll( '[data-type="select-plan"]' ).forEach( ( btn ) => {
            this.addEventListener( btn, 'click', () => {
                const planId = btn.getAttribute( 'data-plan-id' );
                const planName = btn.getAttribute( 'data-plan-name' );
                const price = this._billingCycle === 'yearly'
                    ? btn.getAttribute( 'data-plan-yearly' )
                    : btn.getAttribute( 'data-plan-monthly' );
                this._showConfirmModal( planId, planName, price );
            });
        });

        // Cancel subscription button
        const cancelSubBtn = this.app.view.pageContent.querySelector( '[data-type="cancel-subscription"]' );
        if ( cancelSubBtn )
        {
            this.addEventListener( cancelSubBtn, 'click', () => this._showCancelModal() );
        }

        // Manage billing link
        const manageBillingBtn = this.app.view.pageContent.querySelector( '[data-type="manage-billing"]' );
        if ( manageBillingBtn )
        {
            this.addEventListener( manageBillingBtn, 'click', ( e ) => {
                e.preventDefault();
                const url = this.premiumData?.customerPortalUrl;
                if ( url )
                {
                    window.open( url, '_blank' );
                }
            });
        }

        // Modal buttons
        this._bindModalButtons();
    }

    _updatePriceDisplay()
    {
        const isYearly = this._billingCycle === 'yearly';
        const container = this.app.view.pageContent;

        container.querySelectorAll( '[data-type="price-monthly"]' ).forEach( ( el ) => {
            el.style.display = isYearly ? 'none' : '';
        });

        container.querySelectorAll( '[data-type="price-yearly"]' ).forEach( ( el ) => {
            el.style.display = isYearly ? '' : 'none';
        });

        const monthlyLabel = container.querySelector( '[data-type="billing-label-monthly"]' );
        const yearlyLabel = container.querySelector( '[data-type="billing-label-yearly"]' );
        if ( monthlyLabel ) monthlyLabel.classList.toggle( 'text-muted', isYearly );
        if ( yearlyLabel ) yearlyLabel.classList.toggle( 'text-muted', !isYearly );
    }

    _bindModalButtons()
    {
        // Confirm plan modal buttons
        const closeBtn = this.app.view.pageContent.querySelector( '[data-type="plan-modal-close"]' );
        const cancelBtn = this.app.view.pageContent.querySelector( '[data-type="plan-modal-cancel"]' );
        const confirmBtn = this.app.view.pageContent.querySelector( '[data-type="plan-modal-confirm"]' );

        if ( closeBtn )
        {
            this.addEventListener( closeBtn, 'click', () => this._hideModal( 'confirmPlanModal' ) );
        }

        if ( cancelBtn )
        {
            this.addEventListener( cancelBtn, 'click', () => this._hideModal( 'confirmPlanModal' ) );
        }

        if ( confirmBtn )
        {
            this.addEventListener( confirmBtn, 'click', () => this._confirmPlanChange() );
        }

        // Cancel subscription modal buttons
        const cancelSubClose = this.app.view.pageContent.querySelector( '[data-type="cancel-modal-close"]' );
        const cancelSubCancel = this.app.view.pageContent.querySelector( '[data-type="cancel-modal-cancel"]' );
        const cancelSubConfirm = this.app.view.pageContent.querySelector( '[data-type="cancel-modal-confirm"]' );

        if ( cancelSubClose )
        {
            this.addEventListener( cancelSubClose, 'click', () => this._hideModal( 'cancelSubscriptionModal' ) );
        }

        if ( cancelSubCancel )
        {
            this.addEventListener( cancelSubCancel, 'click', () => this._hideModal( 'cancelSubscriptionModal' ) );
        }

        if ( cancelSubConfirm )
        {
            this.addEventListener( cancelSubConfirm, 'click', () => this._confirmCancelSubscription() );
        }
    }

    _showConfirmModal( planId, planName, price )
    {
        this._selectedPlanId = planId;
        this._selectedPlanName = planName;
        this._selectedBillingCycle = this._billingCycle;

        const cycleLabel = this._billingCycle === 'yearly' ? 'yearly' : 'monthly';
        const priceLabel = price > 0 ? `$${price}/mo (billed ${cycleLabel})` : 'Free';

        const hasSubscription = !!this.premiumData?.lsSubscriptionId;
        const actionLabel = hasSubscription ? 'Change' : 'Subscribe to';

        // Update modal content
        this.app.view.pageContent.querySelectorAll( '[data-type="confirm-plan-name"]' ).forEach( ( el ) => {
            el.textContent = planName;
        });
        this.app.view.pageContent.querySelectorAll( '[data-type="confirm-plan-name-bold"]' ).forEach( ( el ) => {
            el.textContent = planName;
        });
        this.app.view.pageContent.querySelectorAll( '[data-type="confirm-plan-price"]' ).forEach( ( el ) => {
            el.textContent = priceLabel;
        });
        this.app.view.pageContent.querySelectorAll( '[data-type="confirm-action-label"]' ).forEach( ( el ) => {
            el.textContent = actionLabel;
        });

        this._showModal( 'confirmPlanModal' );
    }

    _showCancelModal()
    {
        this._showModal( 'cancelSubscriptionModal' );
    }

    _showModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        if ( modal )
        {
            modal.classList.add( 'show' );
            modal.style.display = 'block';
            modal.setAttribute( 'aria-hidden', 'false' );
            modal.setAttribute( 'aria-modal', 'true' );
            modal.setAttribute( 'role', 'dialog' );
            document.body.classList.add( 'modal-open' );

            const backdrop = document.createElement( 'div' );
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = `${modalId}Backdrop`;
            document.body.appendChild( backdrop );
        }
    }

    _hideModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        const backdrop = document.getElementById( `${modalId}Backdrop` );

        if ( modal )
        {
            modal.classList.remove( 'show' );
            modal.style.display = 'none';
            modal.setAttribute( 'aria-hidden', 'true' );
            modal.removeAttribute( 'aria-modal' );
            document.body.classList.remove( 'modal-open' );
        }

        if ( backdrop )
        {
            backdrop.remove();
        }
    }

    async _confirmPlanChange()
    {
        if ( !this._selectedPlanId )
        {
            return;
        }

        // FREE plan selection — no payment needed
        if ( this._selectedPlanId === 'FREE' )
        {
            this._hideModal( 'confirmPlanModal' );
            return;
        }

        const modal = this.app.view.pageContent.querySelector( '#confirmPlanModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Processing...' );

        try
        {
            const hasSubscription = !!this.premiumData?.lsSubscriptionId;

            if ( hasSubscription && ![ 'expired', 'unpaid' ].includes( this.premiumData?.status ) )
            {
                // Upgrade / Downgrade — update existing subscription
                const { result } = await this.app.api.patch( '/billing/subscription', {
                    planId: this._selectedPlanId,
                    billingCycle: this._selectedBillingCycle
                });

                this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
                this._hideModal( 'confirmPlanModal' );

                if ( result.status === 200 )
                {
                    this.app.view.toast( 'Plan change initiated. It will be updated shortly.', 'success' );

                    // Wait for webhook, then refresh
                    setTimeout( () => this._refreshOrganisationData(), 3000 );
                }
                else
                {
                    this.app.view.toast( result.data?.message || 'Failed to update subscription.', 'error' );
                }
            }
            else
            {
                // New subscription — load lemon.js (first checkout only) while creating the checkout
                const lemonJsLoaded = loadLemonJs().then( () => true, () => false );
                const { result } = await this.app.api.post( '/billing/checkout', {
                    planId: this._selectedPlanId,
                    billingCycle: this._selectedBillingCycle
                });
                const isLemonJsLoaded = await lemonJsLoaded;

                this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
                this._hideModal( 'confirmPlanModal' );

                if ( result.status === 200 && result.data?.checkoutUrl && !isLemonJsLoaded )
                {
                    this.app.view.toast( 'The payment window could not be loaded. Please check your internet connection, allow app.lemonsqueezy.com in content or ad blockers and try again.', 'error' );
                }
                else if ( result.status === 200 && result.data?.checkoutUrl )
                {
                    // Initialize the LemonSqueezy overlay before opening
                    this._setupLemonSqueezy();

                    if ( window.LemonSqueezy?.Url )
                    {
                        window.LemonSqueezy.Url.Open( result.data.checkoutUrl );
                    }
                    else
                    {
                        // Fallback: open checkout in an iframe overlay
                        this._openCheckoutOverlay( result.data.checkoutUrl );
                    }
                }
                else
                {
                    this.app.view.toast( result.data?.message || 'Failed to create checkout.', 'error' );
                }
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this._hideModal( 'confirmPlanModal' );
            this.app.view.toast( 'An error occurred while processing subscription.', 'error' );
        }
    }

    async _confirmCancelSubscription()
    {
        const modal = this.app.view.pageContent.querySelector( '#cancelSubscriptionModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Cancelling...' );

        try
        {
            const { result } = await this.app.api.delete( '/billing/subscription' );

            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this._hideModal( 'cancelSubscriptionModal' );

            if ( result.status === 200 )
            {
                this.app.view.toast( 'Cancellation requested. Your subscription status will update shortly.', 'info' );

                // Wait for webhook, then refresh
                setTimeout( () => this._refreshOrganisationData(), 3000 );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to cancel subscription.', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this._hideModal( 'cancelSubscriptionModal' );
            this.app.view.toast( 'An error occurred while cancelling subscription.', 'error' );
        }
    }

    _openCheckoutOverlay( url )
    {
        const overlay = document.createElement( 'div' );
        overlay.id = 'ls-checkout-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;';

        const closeBtn = document.createElement( 'button' );
        closeBtn.textContent = '\u00D7';
        closeBtn.style.cssText = 'position:absolute;top:16px;right:24px;background:none;border:none;color:#fff;font-size:36px;cursor:pointer;z-index:1;';
        closeBtn.onclick = () =>
        {
            this._closeCheckoutOverlay();
            this._refreshOrganisationData().catch( () => {} );
        };

        const iframe = document.createElement( 'iframe' );
        iframe.src = url;
        iframe.style.cssText = 'width:100%;max-width:480px;height:90vh;border:none;border-radius:12px;';

        // Intercept when iframe navigates to our redirect URL (means checkout completed)
        iframe.addEventListener( 'load', () =>
        {
            try
            {
                // If iframe navigated to our origin, the checkout is done
                const location = iframe.contentWindow?.location;
                if ( location?.origin === window.location.origin && location.pathname === '/premium' &&
                    new URLSearchParams( location.search ).has( 'success' ) )
                {
                    this._closeCheckoutOverlay();
                    this._onCheckoutSuccess();
                }
            }
            catch ( e )
            {
                // Cross-origin access blocked — iframe is still on LemonSqueezy, that's fine
            }
        });

        overlay.appendChild( closeBtn );
        overlay.appendChild( iframe );
        document.body.appendChild( overlay );
    }

    _closeCheckoutOverlay()
    {
        const overlay = document.getElementById( 'ls-checkout-overlay' );
        if ( overlay )
        {
            overlay.remove();
        }
    }

    _showSuccessModal()
    {
        const overlay = document.createElement( 'div' );
        overlay.id = 'success-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;';

        const card = document.createElement( 'div' );
        card.style.cssText = 'background:var(--bs-card-bg, #1a1a2e);border-radius:16px;padding:48px 40px;text-align:center;max-width:420px;width:90%;position:relative;overflow:hidden;';

        card.innerHTML = `
            <div style="width:80px;height:80px;border-radius:50%;background:rgba(25,135,84,.15);display:flex;align-items:center;justify-content:center;margin:0 auto 20px;">
                <iconify-icon icon="solar:check-circle-bold" style="font-size:48px;color:#198754;"></iconify-icon>
            </div>
            <h4 style="margin-bottom:8px;font-weight:700;">Subscription Activated!</h4>
            <p style="color:var(--bs-secondary-color, #adb5bd);margin-bottom:24px;">Your premium plan is now active. Enjoy all the features!</p>
            <button id="success-modal-close" class="btn btn-primary fw-bolder px-5 py-2">Let's go!</button>
        `;

        overlay.appendChild( card );
        document.body.appendChild( overlay );

        document.getElementById( 'success-modal-close' ).onclick = () =>
        {
            overlay.remove();
        };

        this._launchConfetti( overlay );
    }

    _launchConfetti( container )
    {
        const colors = [ '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff922b', '#cc5de8', '#20c997', '#339af0' ];
        const canvas = document.createElement( 'canvas' );
        canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
        container.insertBefore( canvas, container.firstChild );

        const ctx = canvas.getContext( '2d' );
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;

        const pieces = [];
        for ( let i = 0; i < 120; i++ )
        {
            pieces.push( {
                x: canvas.width / 2,
                y: canvas.height / 2,
                vx: ( Math.random() - 0.5 ) * 16,
                vy: ( Math.random() - 0.7 ) * 16,
                w: Math.random() * 8 + 4,
                h: Math.random() * 6 + 2,
                color: colors[ Math.floor( Math.random() * colors.length ) ],
                rotation: Math.random() * 360,
                rotationSpeed: ( Math.random() - 0.5 ) * 12,
                gravity: 0.15 + Math.random() * 0.1,
                opacity: 1
            } );
        }

        const animate = () =>
        {
            ctx.clearRect( 0, 0, canvas.width, canvas.height );
            let alive = false;

            for ( const p of pieces )
            {
                p.x += p.vx;
                p.y += p.vy;
                p.vy += p.gravity;
                p.vx *= 0.99;
                p.rotation += p.rotationSpeed;

                if ( p.y > canvas.height - 20 )
                {
                    p.opacity -= 0.02;
                }

                if ( p.opacity <= 0 ) continue;
                alive = true;

                ctx.save();
                ctx.globalAlpha = p.opacity;
                ctx.translate( p.x, p.y );
                ctx.rotate( ( p.rotation * Math.PI ) / 180 );
                ctx.fillStyle = p.color;
                ctx.fillRect( -p.w / 2, -p.h / 2, p.w, p.h );
                ctx.restore();
            }

            if ( alive )
            {
                requestAnimationFrame( animate );
            }
            else
            {
                canvas.remove();
            }
        };

        requestAnimationFrame( animate );
    }

    async _refreshOrganisationData()
    {
        const organisationId = this.app.user.organisationId;
        const { result } = await this.app.api.get( `/organisation/${organisationId}` );
        if ( !this._exited && result.status === 200 && result.data )
        {
            this.app.organisation.setValues( result.data );
            this.premiumData = result.data.premium || null;
            this.currentPackage = this.premiumData?.id || 'FREE';
            this._render();
            return true;
        }
        return false;
    }

    async exit()
    {
        this._exited = true;
        this._closeCheckoutOverlay();
    }

    getContentHeaderData()
    {
        return {
            title: 'Premium',
            breadcrumb: [ 'Admin', 'Premium' ]
        };
    }
}
